import { useMemo } from 'react';
import { NavSectionLeaf } from '@tanium/react-workbench';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Icon, CloudIcon, RegistryIcon, AdminIcon } from '@tanium/react-graphics';
import { SideNavType } from './components/SideNav/types';

export interface NavRoute extends NavSectionLeaf {
  routeType: SideNavType;
}

interface Routes {
  WORKSPACE: { sectionName: string; pageName: string; url: string; icon: Icon };
  ROUTES: NavSectionLeaf[];
}

const getHomeRoutes = (t: TFunction): NavRoute[] => {
  const MANAGE_ACCOUNT_ROUTE: NavRoute = {
    name: t('Manage Account'),
    route: 'manage-account',
    url: '/',
    routeType: SideNavType.Home,
  };
  const TENANT_HISTORY_ROUTE: NavRoute = {
    name: t('Account History'),
    route: 'event-history',
    url: '/event-history',
    routeType: SideNavType.Home,
  };

  return [MANAGE_ACCOUNT_ROUTE, TENANT_HISTORY_ROUTE];
};

export const getCloudInstanceRoutes = (t: TFunction): NavRoute[] => {
  const INSTANCE_OVERVIEW_ROUTE: NavRoute = {
    name: t('Overview'),
    route: 'instance',
    url: '/instance/overview',
    routeType: SideNavType.Cloud,
  };
  const ADMINISTRATION_ROUTE: NavRoute = {
    name: t('Administration'),
    route: 'admin',
    url: '/instance/admin',
    routeType: SideNavType.Cloud,
  };
  const ENVIRONMENT_STATUS_ROUTE: NavRoute = {
    name: t('Environment Status'),
    route: 'environment-status',
    url: '/instance/environment-status',
    routeType: SideNavType.Cloud,
  };
  const EVENT_HISTORY_ROUTE: NavRoute = {
    name: t('Event History'),
    route: 'event-history',
    url: '/instance/event-history',
    routeType: SideNavType.Cloud,
  };
  const NETWORK_EGRESS_ROUTE: NavRoute = {
    name: t('Network Egress'),
    route: 'egress-allow-list',
    url: '/instance/egress-allow-list',
    routeType: SideNavType.Cloud,
  };

  return [
    INSTANCE_OVERVIEW_ROUTE,
    ADMINISTRATION_ROUTE,
    ENVIRONMENT_STATUS_ROUTE,
    EVENT_HISTORY_ROUTE,
    NETWORK_EGRESS_ROUTE,
  ];
};

const getArtifactsRoutes = (t: TFunction): NavRoute[] => {
  const overview: NavRoute = {
    name: t('Overview'),
    route: 'overview',
    url: '/artifacts/overview',
    routeType: SideNavType.Artifacts,
  };
  return [overview];
};

export const useRoutes = (): Routes => {
  const { t } = useTranslation();
  const location = useLocation();
  const ampRoutes = getHomeRoutes(t);
  const cloudRoutes = getCloudInstanceRoutes(t);
  const artifactsRoutes = getArtifactsRoutes(t);

  const currNavRoute = useMemo((): NavRoute | undefined => {
    const { pathname } = location;
    const routes = [...cloudRoutes, ...artifactsRoutes, ...ampRoutes];
    const currRoute = routes.find((route) => pathname.toLowerCase() === route.url.toString());
    return currRoute;
  }, [ampRoutes, artifactsRoutes, cloudRoutes, location]);

  const currPageName = currNavRoute?.name || '';

  let sectionName: string;
  let icon: Icon;
  let url: string;
  let routes: NavSectionLeaf[];
  switch (currNavRoute?.routeType) {
    case SideNavType.Artifacts:
      sectionName = t('Artifacts');
      url = '/artifacts';
      icon = RegistryIcon;
      routes = artifactsRoutes;
      break;
    case SideNavType.Cloud:
      sectionName = t('Cloud Instances');
      url = '/instance/overview';
      icon = CloudIcon;
      routes = cloudRoutes;
      break;
    default:
      sectionName = t('Tanium Account');
      icon = AdminIcon;
      url = '/';
      routes = ampRoutes;
      break;
  }

  return {
    WORKSPACE: { sectionName, pageName: currPageName, url, icon },
    ROUTES: routes,
  };
};
